import { logger } from '@vista-digital/occ-domain';
import { useDebounceAsyncCallback, useUpdateEffect } from '@vista-digital/react-tools';
import { useCallback, useRef, useState } from 'react';
import { CanceledError, createCancelablePromise } from '../utils/cancelable-promises';
/**
 * Returns a callback which validates the field, and the corresponding state.
 *
 * @param validator the field validator.
 * @param interval the debounce interval. If not provided, defaults to 750.
 * @returns `onValueChanged` the callback to conditionally validate the field when the value changes.
 * @returns `onBlur` the callback to validate the field after blur.
 * @returns `isValidating` a boolean that indicates whether the field is currently being validated.
 */
export function useFieldValidator(validator, setHelpText, errorMessage, interval) {
    const [isValidating, setIsValidating] = useState(false);
    const [isTouched, setIsTouched] = useState(false);
    const prevPromise = useRef();
    // TODO: Add tests #289774
    useUpdateEffect(() => {
        setHelpText === null || setHelpText === void 0 ? void 0 : setHelpText(errorMessage);
    }, [errorMessage]);
    const validateField = useCallback(async (value) => {
        setIsValidating(true);
        const cancelablePromise = createCancelablePromise(validator(value));
        prevPromise.current = cancelablePromise;
        await cancelablePromise.promise.catch(e => {
            if (!(e instanceof CanceledError))
                logger.error(`Failed to validate. ${e}`);
        });
        setIsValidating(false);
    }, [prevPromise, setIsValidating, validator]);
    const debouncedValidation = useDebounceAsyncCallback(validateField, interval);
    const onValueChangedValidation = (newValue, isInvalid) => {
        var _a;
        if (isTouched || isInvalid) {
            (_a = prevPromise.current) === null || _a === void 0 ? void 0 : _a.cancel();
            debouncedValidation(newValue)();
        }
    };
    const onBlurValidation = (value) => {
        setIsTouched(true);
        validateField(value);
    };
    return { onValueChangedValidation, onBlurValidation, isValidating };
}
//# sourceMappingURL=use-field-validator.js.map